/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';
import { AxiosError, AxiosResponse } from 'axios';

import { ApiError } from './APIResponse';

export enum ApiStatusCodes {
  PROCESSING_ERROR = 400,
  AUTHENTICATION_ERROR = 401,
  VALIDATION_ERROR = 422,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE_ERROR = 503
}

export const prepareAxiosError = (err: unknown): ApiError => {
  if ((err as AxiosError).isAxiosError) {
    const error = err as AxiosError<ApiError>;

    if (error.message === 'Network Error' || error.code === 'ECONNABORTED')
      return handleNetworkError(error);

    if (error && error.stack && error.message && !error.response)
      return handleNoResponseError(error);

    const errorResponse = error.response as AxiosResponse<ApiError>;

    if (
      [
        ApiStatusCodes.PROCESSING_ERROR,
        ApiStatusCodes.VALIDATION_ERROR
      ].includes(errorResponse.status)
    )
      return { ...errorResponse.data, isApiError: true };

    if (errorResponse.status === ApiStatusCodes.AUTHENTICATION_ERROR)
      return handleAuthenticationError(errorResponse);

    return handleUnexpectedError(errorResponse);
  } else {
    return handleClientSideError(err);
  }
};

const handleNetworkError = (error: AxiosError) => {
  const details = {
    status: ApiStatusCodes.SERVICE_UNAVAILABLE_ERROR,
    message: `There was a problem connecting to our servers at this time. You may be offline or have a poor signal.`
  };

  Sentry.captureMessage('Network Error', {
    extra: { ...details, actual: error }
  });

  return {
    isApiError: false,
    ...details
  };
};

const handleNoResponseError = (error: AxiosError<ApiError>): ApiError => {
  const details = {
    status: ApiStatusCodes.SERVICE_UNAVAILABLE_ERROR, // TODO: no need?
    message: `There was a problem handling your request at this time.`
  };

  Sentry.captureMessage('NoResponse error', {
    extra: { ...details, actual: error }
  });

  return {
    isApiError: true,
    ...details
  };
};

const handleAuthenticationError = (
  error: AxiosResponse<ApiError>
): ApiError => {
  const authToken = localStorage.getItem('authToken');
  const refreshToken = localStorage.getItem('refreshToken');

  Sentry.captureMessage('Authentication Error', {
    extra: {
      authToken,
      refreshToken,
      actual: error.data
    }
  });

  return {
    isApiError: true,
    message: 'Your session has expired. Please login in again.'
  };
};

const handleUnexpectedError = (error: AxiosResponse<ApiError>) => {
  Sentry.captureMessage('Unexpected Error', {
    extra: error.data,
    level: Sentry.Severity.Fatal
  });

  return {
    isApiError: true,
    message: error.data.message
  };
};

const handleClientSideError = (error: unknown) => {
  const details = {
    status: ApiStatusCodes.INTERNAL_SERVER_ERROR,
    message:
      (
        error as {
          message?: string;
        }
      ).message ??
      `There was a problem handling your request. Please try refreshing the page or close the app and reopen.`
  };

  Sentry.captureException(error, {
    extra: { ...details },
    level: Sentry.Severity.Fatal
  });

  return {
    isApiError: false,
    ...details
  };
};
